import type { MappingCorridor } from '@/entities/dashboard/mapping';
import type { LocationModel } from '@/entities/location';
import type { CorridorDisplay } from '../model/types';

export const CORRIDOR_STATUS_COLORS = {
  /** green - recent (<=1 day) */
  recent: '#10b981',
  /** amber - few days (2-3 days) */
  few_days: '#f59e0b',
  /** red - week old (4-7 days) */
  week: '#ef4444',
  /** gray - old (>7 days) */
  old: '#6b7280'
} as const;

export const CORRIDOR_LINE_WIDTH = {
  /** large - >1000 LTU */
  large: 6,
  /** medium - 500-1000 LTU */
  medium: 4,
  /** small - 100-500 LTU */
  small: 3,
  /** minimal - <100 LTU */
  minimal: 2
} as const;

export function getCorridorStatusColor(daysSinceDispatch: number): string {
  if (daysSinceDispatch <= 1) return CORRIDOR_STATUS_COLORS.recent;
  if (daysSinceDispatch <= 3) return CORRIDOR_STATUS_COLORS.few_days;
  if (daysSinceDispatch <= 7) return CORRIDOR_STATUS_COLORS.week;
  return CORRIDOR_STATUS_COLORS.old;
}

export function getCorridorLineWidth(ltuQty: number): number {
  if (ltuQty > 1000) return CORRIDOR_LINE_WIDTH.large;
  if (ltuQty > 500) return CORRIDOR_LINE_WIDTH.medium;
  if (ltuQty > 100) return CORRIDOR_LINE_WIDTH.small;
  return CORRIDOR_LINE_WIDTH.minimal;
}

export function isCorridorInTransit(daysSinceDispatch: number): boolean {
  return daysSinceDispatch >= 0;
}

export function createCorridorId(from: string, to: string): string {
  return `${from}-${to}`;
}

export function createCorridorDisplay(
  corridor: MappingCorridor,
  fromLocation: LocationModel,
  toLocation: LocationModel
): CorridorDisplay {
  return {
    id: createCorridorId(corridor.from, corridor.to),
    from: fromLocation,
    to: toLocation,
    consignments: corridor.consignments,
    daysSinceDispatch: corridor.daysSinceDispatch,
    vehicleCategory: corridor.vehicleCategory,
    dispatchDate: corridor.dispatchDate,
    ltuQty: corridor.ltuQty,
    distance: fromLocation.distanceTo(toLocation),
    isActive: isCorridorInTransit(corridor.daysSinceDispatch),
    color: getCorridorStatusColor(corridor.daysSinceDispatch),
    lineWidth: getCorridorLineWidth(corridor.ltuQty)
  };
}

export function calculateCorridorStatistics(corridors: MappingCorridor[]) {
  const totalConsignments = corridors.reduce((acc, corridor) => {
    const count = corridor.consignments.split(',').length;
    return acc + count;
  }, 0);

  const totalLTU = corridors.reduce((acc, corridor) => acc + corridor.ltuQty, 0);

  const byVehicleCategory = corridors.reduce(
    (acc, corridor) => {
      acc[corridor.vehicleCategory] = (acc[corridor.vehicleCategory] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const activeCorridors = corridors.filter((corridor) => isCorridorInTransit(corridor.daysSinceDispatch)).length;

  return {
    totalCorridors: corridors.length,
    activeCorridors,
    totalConsignments,
    totalLTU: Math.round(totalLTU),
    byVehicleCategory
  };
}
